<script setup>
import SimpleTable from "@/components/table/SimpleTable.vue";
import ModalApiKey from "@/views/security/components/ModalApiKey.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonSearcherSimplified from "@/components/searcher/ArgonSearcherSimplified.vue";
import { ref, onMounted, watch, inject } from "vue";
import { apiKeyStore } from "@/_store/apikeyStore.js";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import moment from "moment";
import CopyValue from "@/components/CopyValue.vue";
import { errorUtils } from "@/utils";

const sweetAlert = inject("$swal");
const visibleKeys = ref([]);
const snackbar = ref(null);
const apiStore = apiKeyStore();
const showModal = ref(false);
let copiedKey = ref(false);
const searchQuery = ref(""); 

const headers = [
  { header: "Nombre", field: "appName", sort: false, width: "w-28" },
  { header: "Api Key", field: "apiToken", sort: false, width: "w-60" },
  { header: "Creado", field: "createdAt", sort: false },
  { header: "", sort: false },
];

const listTable = ref({ data: [] });
const filteredData = ref([]); 

function getApiKey() {
  apiStore.getApiKeys();
}

function openModal() {
  showModal.value = true;
}

function onApiKeySuccess() {
  getApiKey();
}



function deleteApiKey(item) {
  sweetAlert({
    title: "¿Está seguro que desea eliminar la api key?",
    text: "¡No podrá revertir esta acción!",
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: `btn btn-xs  ms-1 bg-gradient-danger`,
      cancelButton: "btn btn-xs  btn-outline-primary me-1",
    },
    buttonsStyling: false,
  }).then((result) => {
    if (result.isConfirmed) apiStore.deleteApiKeys(item);
  });
}

function getPosition(field) {
  const column = headers.find((col) => col.field === field);
  return column && column.position ? column.position : "start";
}

function handleSearch(query) {
  searchQuery.value = query;
  filterData();
}

function filterData() {
  if (!searchQuery.value) {
    filteredData.value = listTable.value.data;
    return;
  }
  
  const searchLower = searchQuery.value.toLowerCase();
  filteredData.value = listTable.value.data.filter(item => 
    item.appName.toLowerCase().includes(searchLower)
  );
}

watch(() => apiStore.error, (value) => {
  if (value) {
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
});

watch(() => apiStore.delete, (value) => {
  if (value) {
    snackbar.value = {
      title: "Eliminado",
      message: "Se ha eliminado el api key",
      type: "info",
      color: "white",
    };
    apiStore.delete = null;
    getApiKey();
  }
});

watch(() => apiStore.list, (value) => {
  if (value) {
    listTable.value.data = value;
    filterData(); 
  }
});

const closeSnackbar = () => {
  snackbar.value = null;
};

onMounted(() => {
  getApiKey();
});
</script>

<template>
  <div class="card h-100">
    <div class="card-header">API Keys</div>
      <div class="card-body">
        <div class="row align-items-center mb-3">
          <div class="col-3">
            <argon-button             
              class="btn-xs"
              color="primary"
              variant="outline" 
              @click="openModal">
              <i class="fa fa-plus me-sm-1"></i>
              Nueva
            </argon-button>
          </div>
          
          <div class="col-9 d-flex justify-content-end">
            <div>
              <argon-searcher-simplified
                @search="handleSearch"
              />
            </div>
          </div>
        </div>
        
        <div class="row">
          <div class="col style-api">
            <simple-table
              :headers="headers"
              :items="{ data: filteredData }"
            >
              <template #default="{ item }">
                <argon-td
                  :text-secondary="item.appName"
                  :position="getPosition('appName')"
                />
                <argon-td
                  :text-secondary="item.apiToken"
                  :position="getPosition('apiToken')"
                  :has-hide="!visibleKeys.includes(item._id)"                
                />
                <argon-td
                  :position="getPosition('createdAt')"
                  :text-secondary="moment(item.createdAt).format('DD/MM/YYYY')"
                />
                <argon-td>
                  <div class="d-flex text-sm align-items-center">                    
                    <copy-value
                      class="ms-2"
                      :value-to-copy="item.apiToken"
                      :copied-value="copiedKey"
                    />
                    <a href="javascript:;" @click="deleteApiKey(item._id)">
                      <i
                        v-tooltip="'Eliminar'"
                        class="fa fa-trash text-danger text-xs ms-2 hover"
                        
                      ></i>
                    </a>
                  </div>
                </argon-td>
              </template>
            </simple-table>
          </div>
        </div>
    </div>
  </div>

  <modal-api-key
    v-if="showModal"
    @success="onApiKeySuccess"
    @close="showModal = false"
  />

  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
.hover:hover {
  cursor: pointer !important;
}
</style>
